import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { setSelectedElementKey } from "@/store/iframeSlice";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { toast } from "sonner";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { TempoElement } from "@/utils/tempoElement";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { getStoriesForComponent } from "@/utils/extractElementsUtils";
import axios from "axios";
import { JsxElement } from "@/utils/jsxElementTypes";
import { ProjectRunMode } from "@/utils/templateProjects";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useAssetComponents } from "../useAssetComponents";
import { useSession } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

export const useDeleteStoryboards = () => {
  const canvasId = useCanvasId();

  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );
  const { componentKeyToComponent } = useAssetComponents();
  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream
  );

  const { session } = useSession();

  const dispatch = useDispatch<any>();

  const canvas = useActiveCanvas();
  const project = useActiveProject();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );
  // @ts-expect-error TS2538
  const canvasComponent = componentKeyToComponent?.[canvasComponentKey];

  const [confirmDeleteStoryboardIds, setConfirmDeleteStoryboardIds] = useState<
    string[]
  >([]);

  const confirmDeleteStoryboards = useMemo(() => {
    return confirmDeleteStoryboardIds.map((storyboardId) => {
      // @ts-expect-error TS18047
      return canvas.storyboards.find((sb: any) => sb.id === storyboardId);
    });
  }, [confirmDeleteStoryboardIds, canvas]);

  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const deleteStoryboardIds = useCallback(
    async (storyboardIds: string[]) => {
      if (!supabaseClient) {
        console.error("Supabase client not available");
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      const storyboardsToDelete = canvasRef.current.storyboards.filter(
        (sb: any) => {
          return storyboardIds.includes(sb.id);
        }
      );

      const savedStoryboards = Array.from(canvasRef.current.storyboards);
      const storyboardIdsToDelete = new Set(
        storyboardsToDelete.map((sb) => sb.id)
      );

      const onFailure = async () => {
        // Undelete the storyboards
        if (!canvasRef.current) {
          console.error("Canvas reference not available");
          return;
        }
        await updateCanvasStoryboards(
          supabaseClient,
          dispatch,
          savedStoryboards,
          canvasRef.current
        );
      };

      let storyboardsToSet = canvasRef.current.storyboards;

      const storyboardIdsToOnlyDelete = new Set(
        canvasRef.current.storyboards
          .filter(
            (sb: any) =>
              (sb.type === STORYBOARD_TYPES.APPLICATION ||
                (sb.type === STORYBOARD_TYPES.COMPONENT && sb.saved)) &&
              storyboardIdsToDelete.has(sb.id)
          )
          .map((sb: any) => sb.id)
      );

      if (storyboardIdsToOnlyDelete.size) {
        // No corresponding API request that would be undoed, so just delete
        storyboardsToSet = storyboardsToSet.map((sb: any) => {
          if (storyboardIdsToOnlyDelete.has(sb.id)) {
            return {
              ...sb,
              deleted: true,
              deleted_at: new Date().toISOString(),
            };
          }

          return {
            ...sb,
          };
        });
      }

      const storyboardsLeftAfterDelete = storyboardsToSet.filter(
        (sb: any) => !storyboardIdsToDelete.has(sb.id) && !sb.deleted
      );

      // If there are any other storyboards with a particular component filepath, do not delete it on the backend
      const storyboardsToDeleteOnBackend = storyboardsToDelete.filter(
        (sb: any) => {
          if (storyboardIdsToOnlyDelete.has(sb.id)) {
            return false;
          }

          if (sb.type === STORYBOARD_TYPES.APPLICATION) {
            return false;
          }

          if (sb.type === STORYBOARD_TYPES.STORY) {
            return true;
          }

          const filePath = sb.fullFilePath;
          const otherStoryboardsWithComponent =
            storyboardsLeftAfterDelete.filter(
              (storyboard: any) =>
                storyboard.type !== STORYBOARD_TYPES.APPLICATION &&
                storyboard.fullFilePath === filePath
            );

          return otherStoryboardsWithComponent.length === 0;
        }
      );

      const newStoryboards = storyboardsToSet.map((sb: any) => {
        if (!storyboardIdsToDelete.has(sb.id)) {
          return {
            ...sb,
          };
        }

        return {
          ...sb,
          deleted: true,
          deleted_at: new Date().toISOString(),
        };
      });

      if (!canvasRef.current) {
        console.error("Canvas reference not available");
        return;
      }

      await updateCanvasStoryboards(
        supabaseClient,
        dispatch,
        newStoryboards,
        canvasRef.current
      );

      const storyboardsForStoryTypes = storyboardsToDelete.filter(
        (sb: any) => sb.type === STORYBOARD_TYPES.STORY
      );

      if (storyboardsForStoryTypes.length > 0 && !canvasComponent) {
        throw "Deleting story type storyboards can only be allowed when there is a canvas component";
      }

      const stories = getStoriesForComponent(
        project,
        treeElementLookup,
        canvasComponent
      );

      const storyTreeElements = stories.filter((story: JsxElement) => {
        return storyboardsForStoryTypes.find(
          (storyboard: any) => story.componentName === storyboard.name
        );
      });

      // Only continue if needing to delete on the backend
      if (!storyboardsToDeleteOnBackend.length) {
        return;
      }

      const supabaseJWT = await getUnexpiredToken(session);

      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/mutate/deleteUnsavedComponent`,
          {
            storyboardIds: storyboardsToDeleteOnBackend.map((sb: any) => sb.id),
            storyTreeElements: storyTreeElements,
            subCanvasId:
              canvasRef.current.id !== canvasId
                ? canvasRef.current.id
                : undefined,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then(async (res: any) => {
          updateLookupsBasedOnPartialResponse(res.data);
          triggerRefetchActivityStream();
        })
        .catch(async (err) => {
          logAxiosError("Error deleting storyboard", err);
          toast.warning(
            "Warning: Could not delete the storyboard in the codebase: " +
              getAxiosErorrMessage(err)
          );

          onFailure();
        });

      // Clear the selected state
      dispatch(setSelectedElementKey(null));
      return;
    },
    [project, Boolean(session), canvasComponent, treeElementLookup]
  );

  const deleteStoryboards = useCallback(
    async (storyboardElements: TempoElement[]) => {
      const storyboardIdsToDelete = new Set(
        storyboardElements.map((el) => el.storyboardId)
      );

      // @ts-expect-error TS18047
      const hasSavedStories = canvasRef.current.storyboards.find(
        (sb: any) =>
          storyboardIdsToDelete.has(sb.id) &&
          sb.type === STORYBOARD_TYPES.STORY &&
          sb.saved === true
      );

      if (hasSavedStories) {
        setConfirmDeleteStoryboardIds(Array.from(storyboardIdsToDelete));
      } else {
        await deleteStoryboardIds(Array.from(storyboardIdsToDelete));
      }
    },
    [canvasComponentKey, deleteStoryboardIds]
  );

  const renderConfirmDeleteDialog = useCallback(
    () => (
      <AlertDialog open={Boolean(confirmDeleteStoryboardIds.length)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="flex flex-col align-start gap-2">
                <div className="text-secondary-900">
                  This will delete the following stories permanently:
                </div>
                {confirmDeleteStoryboards
                  .filter((storyboard: any) => {
                    return (
                      storyboard.type === STORYBOARD_TYPES.STORY &&
                      storyboard.saved
                    );
                  })
                  .map((storyboard: any) => {
                    return <div>- {storyboard.name}</div>;
                  })}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setConfirmDeleteStoryboardIds([])}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant={"secondary"}
              onClick={async () => {
                setConfirmDeleteStoryboardIds([]);
                await deleteStoryboardIds(confirmDeleteStoryboardIds);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ),
    [confirmDeleteStoryboardIds, confirmDeleteStoryboards, deleteStoryboardIds]
  );

  return {
    deleteStoryboards,
    renderConfirmDeleteDialog,
  };
};
